import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Plus, Trash2, X, Image as ImageIcon, ChevronDown, ChevronUp,
  Package, DollarSign, Upload, Layers, Grid3X3, LayoutGrid,
  Palette, Square, RectangleHorizontal, Calculator, Minus
} from 'lucide-react';

// ====================
// VARIANT IMAGE UPLOAD COMPONENT
// ====================
const VariantImageUpload = ({ value, onChange, size = 'sm' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check authentication before upload
    let token = sessionStorage.getItem('token');
    if (!token) {
      alert('Session expired. Please log in again to upload images.');
      console.error('[VariantImageUpload] No auth token found in localStorage');
      return;
    }

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    // Helper to refresh token if needed
    const refreshTokenIfNeeded = async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          });
          if (response.ok) {
            const data = await response.json();
            if (data.access_token) {
              sessionStorage.setItem('token', data.access_token);
              return data.access_token;
            }
          }
        } catch (e) {
          console.warn('[VariantImageUpload] Token refresh failed:', e);
        }
      }
      return sessionStorage.getItem('token');
    };

    setUploading(true);
    setCompressionProgress(0);
    
    try {
      // Client-side image compression to prevent 504 timeouts
      const compressionOptions = {
        maxSizeMB: 1, // Max file size 1MB
        maxWidthOrHeight: 1920, // Max dimension
        useWebWorker: true,
        onProgress: (progress) => {
          setCompressionProgress(Math.round(progress));
        },
        fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
      };
      
      let compressedFile;
      try {
        compressedFile = await imageCompression(file, compressionOptions);
      } catch (compressionError) {
        console.warn('[VariantImageUpload] Compression failed, using original:', compressionError);
        compressedFile = file; // Fallback to original
      }
      
      // Check final size
      if (compressedFile.size > 5 * 1024 * 1024) {
        alert('Image is too large even after compression. Please use a smaller image.');
        return;
      }
      
      const formData = new FormData();
      formData.append('file', compressedFile, file.name); // Keep original filename
      
      // Use the media upload endpoint for CDN storage
      // DO NOT set Content-Type header - let browser set it with boundary for FormData
      // optimize=false since we already did client-side compression
      let response = await fetch(`${BACKEND_URL}/api/media/upload?asset_type=variant_image&optimize=false`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Content-Type is NOT set - browser will set multipart/form-data with boundary automatically
        },
        body: formData
      });

      // Handle 401 - try to refresh token and retry
      if (response.status === 401) {
        token = await refreshTokenIfNeeded();
        
        // Retry with fresh token
        response = await fetch(`${BACKEND_URL}/api/media/upload?asset_type=variant_image&optimize=false`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[VariantImageUpload] Auth error - token refresh failed');
          alert('Your session has expired. Please refresh the page and log in again.');
          return;
        }
        if (response.status === 504) {
          console.error('[VariantImageUpload] Gateway timeout - server too slow');
          alert('Upload timed out. Please try a smaller image or try again later.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed: ${response.status}`);
      }
      
      const data = await response.json();
      // Use the CDN URL (do_source_url or file_url)
      const imageUrl = data.do_source_url || data.file_url || data.url;
      onChange(imageUrl);
    } catch (error) {
      console.error('[VariantImageUpload] Upload error:', error);
      alert(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      setCompressionProgress(0);
    }
  };

  const sizeClasses = size === 'lg' ? 'h-16 w-16' : 'h-10 w-10';
  const buttonClasses = size === 'lg' ? 'h-16 w-24' : 'h-10 w-20';

  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />
      {value ? (
        <div className={`relative ${sizeClasses} rounded border overflow-hidden group`}>
          <img src={value} alt="variant" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
          >
            <X className="h-4 w-4 text-white" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`${buttonClasses} text-xs`}
        >
          {uploading ? (
            <span className="flex items-center gap-1">
              <span className="animate-spin">⏳</span>
              {compressionProgress > 0 && compressionProgress < 100 ? `${compressionProgress}%` : ''}
            </span>
          ) : (
            <><Upload className="h-3 w-3 mr-1" /> Upload</>
          )}
        </Button>
      )}
    </div>
  );
};

// ====================
// DISPLAY TYPE SELECTOR
// ====================
const DisplayTypeSelector = ({ value, onChange }) => {
  const displayTypes = [
    { value: 'buttons', label: 'Text Buttons', icon: Square, description: 'Simple text buttons' },
    { value: 'swatches', label: 'Image Swatches', icon: Palette, description: 'Small circular/square images' },
    { value: 'cards_scroll', label: 'Scrollable Cards', icon: RectangleHorizontal, description: 'Horizontal scrollable cards' },
    { value: 'cards_rich', label: 'Rich Cards (70/30)', icon: LayoutGrid, description: '70% image, 30% text' },
    { value: 'tabs_grid', label: 'Tabs with Grid', icon: Grid3X3, description: 'Tab structure with image grid' },
  ];

  return (
    <Select value={value || 'buttons'} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select display type" />
      </SelectTrigger>
      <SelectContent>
        {displayTypes.map((type) => (
          <SelectItem key={type.value} value={type.value}>
            <div className="flex items-center gap-2">
              <type.icon className="h-4 w-4" />
              <span>{type.label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// ====================
// QUANTITY STEPPER COMPONENT
// ====================
const QuantityStepper = ({ value, onChange, min = 0, max = 999, disabled = false }) => {
  const handleIncrement = () => {
    const newValue = Math.min((value || 0) + 1, max);
    onChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max((value || 0) - 1, min);
    onChange(newValue);
  };

  const handleInputChange = (e) => {
    const inputValue = parseInt(e.target.value) || 0;
    const clampedValue = Math.max(min, Math.min(max, inputValue));
    onChange(clampedValue);
  };

  return (
    <div className="flex items-center border rounded-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleDecrement}
        disabled={disabled || (value || 0) <= min}
        className="h-8 w-8 p-0 rounded-none border-r"
      >
        <Minus className="h-3 w-3" />
      </Button>
      <Input
        type="number"
        value={value || 0}
        onChange={handleInputChange}
        disabled={disabled}
        min={min}
        max={max}
        className="h-8 w-16 text-center border-0 rounded-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleIncrement}
        disabled={disabled || (value || 0) >= max}
        className="h-8 w-8 p-0 rounded-none border-l"
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
};

// ====================
// MAIN COMPONENT
// ====================
/**
 * VariantManagerV2 - Improved Variant Management System
 * 
 * Key Changes from V1:
 * - No "Master Variant" toggle - all options can have images
 * - Price/Stock always editable at SKU level
 * - Display configuration per variant type
 * - Automatic inventory aggregation
 * 
 * @param {Array} variants - Variant type definitions [{name, options[], display_config}]
 * @param {Array} variantCombinations - Generated SKUs with price/stock/sku
 * @param {number} basePrice - Base price for new combinations
 * @param {string} listingType - 'product' or 'service' (for example display)
 * @param {function} onVariantsChange - Callback when variant definitions change
 * @param {function} onCombinationsChange - Callback when SKU data changes
 * @param {function} onTotalStockChange - Callback with computed total stock
 */
const VariantManagerV2 = ({
  variants = [],
  variantCombinations = [],
  basePrice = 0,
  listingType = 'product',
  onVariantsChange,
  onCombinationsChange,
  onTotalStockChange,
  maxVariants = 3,
  maxOptions = 10,
  hideHeader = false,
  hideAddButton = false,
}) => {
  const [expandedVariants, setExpandedVariants] = useState({});
  const [showSkuTable, setShowSkuTable] = useState(true);
  
  // Use ref to track if we're in an internal update to prevent loops
  const isInternalUpdate = useRef(false);
  const prevVariantsRef = useRef(JSON.stringify(variants));
  // Keep track of the latest variantCombinations to prevent stale data issues
  const latestCombinationsRef = useRef(variantCombinations);
  
  // Update the ref whenever variantCombinations changes
  useEffect(() => {
    latestCombinationsRef.current = variantCombinations;
  }, [variantCombinations]);

  // Calculate total stock from all combinations
  const totalStock = useMemo(() => {
    return variantCombinations.reduce((sum, combo) => sum + (combo.stock || 0), 0);
  }, [variantCombinations]);

  // Notify parent of total stock changes - using ref to prevent infinite loop
  const prevTotalStockRef = useRef(totalStock);
  useEffect(() => {
    if (onTotalStockChange && totalStock !== prevTotalStockRef.current) {
      prevTotalStockRef.current = totalStock;
      onTotalStockChange(totalStock);
    }
  }, [totalStock]);

  // Generate all SKU combinations from variants
  const generateCombinations = useCallback((variantsList, existingCombinations = [], defaultPrice = 0) => {
    if (!variantsList || variantsList.length === 0) return [];

    const validVariants = variantsList.filter(v => 
      v.options && v.options.length > 0 && v.name
    );

    if (validVariants.length === 0) return [];

    // Cartesian product of all options
    const cartesian = (arrays) => {
      if (arrays.length === 0) return [[]];
      return arrays.reduce((acc, curr) => 
        acc.flatMap(a => curr.map(c => [...a, c])), 
        [[]]
      );
    };

    const optionArrays = validVariants.map(v => 
      (v.options || []).map(opt => ({
        variantName: v.name,
        optionName: opt.name || '',
        optionImage: opt.image || '',
        priceImpact: parseFloat(opt.price_impact) || 0,
        quantity: parseInt(opt.quantity) || 1
      }))
    );

    const combinations = cartesian(optionArrays);

    // Ensure defaultPrice is numeric
    const numericDefaultPrice = parseFloat(defaultPrice) || 0;

    return combinations.map(combo => {
      const options = {};
      const images = {};
      const quantities = {};
      let totalPriceImpact = 0;
      
      combo.forEach(c => {
        options[c.variantName] = c.optionName;
        if (c.optionImage) {
          images[c.variantName] = c.optionImage;
        }
        quantities[c.variantName] = c.quantity;
        totalPriceImpact += parseFloat(c.priceImpact) || 0;
      });
      
      const combinationKey = combo.map(c => c.optionName).join('|');
      
      // Preserve existing data if available
      const existing = existingCombinations.find(ec => ec.combination_key === combinationKey);
      
      if (existing) {
        return { ...existing, images, quantities, price_impact: totalPriceImpact };
      }

      return {
        combination_key: combinationKey,
        options,
        images,
        quantities,
        price: numericDefaultPrice + totalPriceImpact,
        price_impact: totalPriceImpact,
        sku: '',
        stock: 0,
        hsn_code: '',
        is_available: true
      };
    });
  }, []);

  // Regenerate combinations when variants structure changes
  useEffect(() => {
    const currentVariantsStr = JSON.stringify(variants.map(v => ({
      name: v.name,
      options: v.options?.map(o => o.name)
    })));
    
    // Only regenerate if variant structure actually changed
    if (currentVariantsStr !== prevVariantsRef.current) {
      prevVariantsRef.current = currentVariantsStr;
      
      if (variants.length > 0 && onCombinationsChange) {
        // Use the ref to get the latest combinations (prevents stale closure issues)
        const existingCombos = latestCombinationsRef.current || [];
        const newCombinations = generateCombinations(variants, existingCombos, basePrice);
        const currentKeys = existingCombos.map(c => c.combination_key).sort().join(',');
        const newKeys = newCombinations.map(c => c.combination_key).sort().join(',');
        
        // Only update if keys actually changed (prevents infinite loops)
        if (currentKeys !== newKeys) {
          isInternalUpdate.current = true;
          onCombinationsChange(newCombinations);
        }
      }
    }
  }, [variants, generateCombinations, basePrice, onCombinationsChange]);

  // Sync stored combination prices when basePrice prop changes (e.g. subscription base_plan_price update)
  const prevBasePriceRef = useRef(basePrice);
  useEffect(() => {
    if (prevBasePriceRef.current === basePrice) return;
    prevBasePriceRef.current = basePrice;
    const combos = latestCombinationsRef.current;
    if (!onCombinationsChange || combos.length === 0) return;
    isInternalUpdate.current = true;
    onCombinationsChange(
      combos.map(combo => ({
        ...combo,
        price: parseFloat(basePrice) + (parseFloat(combo.price_impact) || 0),
      }))
    );
  }, [basePrice]);

  // Add a new variant type
  const addVariant = () => {
    if (variants.length >= maxVariants) {
      alert(`Maximum ${maxVariants} variant types allowed`);
      return;
    }
    
    const newVariant = {
      name: '',
      display_config: 'buttons',
      options: [{ 
        name: '', 
        image: '', 
        price_impact: 0, // 1st option always starts at 0 (matches base price)
        quantity: 1
      }]
    };
    
    onVariantsChange([...variants, newVariant]);
    setExpandedVariants(prev => ({ ...prev, [variants.length]: true }));
  };

  // Remove a variant type
  const removeVariant = (index) => {
    const updated = variants.filter((_, i) => i !== index);
    onVariantsChange(updated);
  };

  // Update variant name or display config
  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    onVariantsChange(updated);
  };

  // Add option to a variant
  const addOption = (variantIndex) => {
    const updated = [...variants];
    const variant = updated[variantIndex];
    
    if ((variant.options?.length || 0) >= maxOptions) {
      alert(`Maximum ${maxOptions} options per variant`);
      return;
    }
    
    updated[variantIndex] = {
      ...variant,
      options: [...(variant.options || []), { 
        name: '', 
        image: '', 
        price_impact: 0, 
        quantity: 1
      }]
    };
    
    onVariantsChange(updated);
  };

  // Remove option from a variant
  const removeOption = (variantIndex, optionIndex) => {
    const updated = [...variants];
    updated[variantIndex] = {
      ...updated[variantIndex],
      options: updated[variantIndex].options.filter((_, i) => i !== optionIndex)
    };
    onVariantsChange(updated);
  };

  // Update an option field
  const updateOption = (variantIndex, optionIndex, field, value) => {
    const updated = [...variants];
    updated[variantIndex] = {
      ...updated[variantIndex],
      options: updated[variantIndex].options.map((opt, i) => 
        i === optionIndex ? { ...opt, [field]: value } : opt
      )
    };
    onVariantsChange(updated);
  };

  // Update a combination (SKU) field
  const updateCombination = (combinationKey, field, value) => {
    const updated = variantCombinations.map(combo =>
      combo.combination_key === combinationKey
        ? { ...combo, [field]: value }
        : combo
    );
    onCombinationsChange(updated);
  };

  // Get primary image for a combination (first available option image)
  const getCombinationImage = (combination) => {
    if (!combination?.options) return null;
    for (const [variantName, optionName] of Object.entries(combination.options)) {
      const variant = variants.find(v => v.name === variantName);
      if (variant) {
        const option = variant.options?.find(opt => opt.name === optionName);
        if (option?.image) return option.image;
      }
    }
    return null;
  };

  // Contextual labels based on listing type
  const variantLabel = listingType === 'service' ? 'Service Packages' : 
                       listingType === 'rental' ? 'Rental Options' : 
                       listingType === 'event' ? 'Ticket Types' : 'Product Variants';
  const variantDescription = listingType === 'service' ? 'Define service packages like Duration, Level' : 
                             listingType === 'rental' ? 'Define rental options like Duration, Size' : 
                             listingType === 'event' ? 'Define ticket types like General, VIP' : 
                             'Define variant options like Color, Size, Material';

  return (
    <div className="space-y-6">
      {/* Header with Add Variant Button - Only show if not hidden */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Layers className="h-5 w-5" />
              {variantLabel}
            </h3>
            <p className="text-sm text-gray-500">
              {variantDescription}
            </p>
          </div>
          {!hideAddButton && (
            <Button onClick={addVariant} disabled={variants.length >= maxVariants} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Variant Type
            </Button>
          )}
        </div>
      )}

      {/* Add Variant Button - Show when header is hidden and we need the button */}
      {hideHeader && variants.length > 0 && variants.length < maxVariants && (
        <Button onClick={addVariant} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Another Variant Type ({variants.length}/{maxVariants})
        </Button>
      )}

      {/* Variant Types */}
      <div className="space-y-4">
        {variants.map((variant, variantIndex) => (
          <Card key={variantIndex} className="overflow-hidden">
            <CardHeader className="py-3 px-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {/* Variant Name */}
                <div className="flex-1">
                  <Input
                    value={variant.name || ''}
                    onChange={(e) => updateVariant(variantIndex, 'name', e.target.value)}
                    placeholder="Variant Name (e.g., Color, Size)"
                    className="font-medium"
                  />
                </div>
                
                {/* Display Configuration */}
                <div className="w-full sm:w-48">
                  <DisplayTypeSelector
                    value={variant.display_config}
                    onChange={(value) => updateVariant(variantIndex, 'display_config', value)}
                  />
                </div>
                
                {/* Show Quantity Toggle - Only for tabs_grid display type */}
                {variant.display_config === 'tabs_grid' && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`showQuantity-${variantIndex}`}
                      checked={variant.showQuantity || false}
                      onCheckedChange={(checked) => updateVariant(variantIndex, 'showQuantity', checked)}
                    />
                    <Label 
                      htmlFor={`showQuantity-${variantIndex}`}
                      className="text-xs text-gray-600 cursor-pointer"
                    >
                      Show Qty
                    </Label>
                  </div>
                )}
                
                {/* Expand/Collapse & Delete */}
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedVariants(prev => ({
                      ...prev,
                      [variantIndex]: !prev[variantIndex]
                    }))}
                  >
                    {expandedVariants[variantIndex] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeVariant(variantIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {expandedVariants[variantIndex] && (
              <CardContent className="pt-4">
                {/* Option Headers - Hidden on mobile, dynamic based on showQuantity */}
                <div className={`hidden sm:grid ${variant.showQuantity ? 'grid-cols-[1fr_100px_80px_100px_120px_40px]' : 'grid-cols-[1fr_100px_100px_120px_40px]'} gap-3 mb-2 px-1`}>
                  <Label className="text-xs font-semibold text-gray-600 uppercase">Option Name</Label>
                  <Label className="text-xs font-semibold text-gray-600 uppercase">Image</Label>
                  {variant.showQuantity && (
                    <Label className="text-xs font-semibold text-gray-600 uppercase">Quantity</Label>
                  )}
                  <Label className="text-xs font-semibold text-gray-600 uppercase">Price +/-</Label>
                  <Label className="text-xs font-semibold text-gray-600 uppercase">Final Price</Label>
                  <div></div>
                </div>

                {/* Options */}
                <div className="space-y-3 sm:space-y-2">
                  {(variant.options || []).map((option, optionIndex) => (
                    <div 
                      key={optionIndex}
                      className={`flex flex-col gap-3 p-3 bg-gray-50 rounded-lg sm:grid ${variant.showQuantity ? 'sm:grid-cols-[1fr_100px_80px_100px_120px_40px]' : 'sm:grid-cols-[1fr_100px_100px_120px_40px]'} sm:gap-3 sm:items-center sm:p-2`}
                    >
                      {/* Option Name */}
                      <div className="space-y-1 sm:space-y-0">
                        <Label className="text-xs font-medium text-gray-500 sm:hidden">Option Name</Label>
                        <Input
                          value={option.name || ''}
                          onChange={(e) => updateOption(variantIndex, optionIndex, 'name', e.target.value)}
                          placeholder="e.g., Red, Large, Silk"
                        />
                      </div>
                      
                      {/* Option Image */}
                      <div className="flex items-center gap-2 sm:block">
                        <Label className="text-xs font-medium text-gray-500 sm:hidden">Image:</Label>
                        <VariantImageUpload
                          value={option.image || ''}
                          onChange={(url) => updateOption(variantIndex, optionIndex, 'image', url)}
                        />
                      </div>
                      
                      {/* Quantity Stepper - Only show if showQuantity is enabled */}
                      {variant.showQuantity && (
                        <div className="space-y-1 sm:space-y-0">
                          <Label className="text-xs font-medium text-gray-500 sm:hidden">Quantity</Label>
                          <QuantityStepper
                            value={option.quantity || 1}
                            onChange={(value) => updateOption(variantIndex, optionIndex, 'quantity', value)}
                            min={0}
                            max={999}
                          />
                        </div>
                      )}
                      
                      {/* Price +/- Input */}
                      <div className="space-y-1 sm:space-y-0">
                        <Label className="text-xs font-medium text-gray-500 sm:hidden">Price +/-</Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                          <Input
                            type="number"
                            step="1"
                            value={option.price_impact || 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              // Enforce: 1st option of each variant = 0
                              if (optionIndex === 0) return;
                              updateOption(variantIndex, optionIndex, 'price_impact', val);
                            }}
                            disabled={optionIndex === 0}
                            placeholder="0"
                            className={`pl-6 w-28 ${optionIndex === 0 ? 'bg-gray-100 text-gray-400' : ''}`}
                            title={optionIndex === 0 ? 'First option is the base — price difference is always 0' : 'Price adjustment from base'}
                          />
                        </div>
                      </div>
                      
                      {/* Editable Final Price */}
                      <div className="flex items-center gap-2 sm:block">
                        <Label className="text-xs font-medium text-gray-500 sm:hidden">Final Price</Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                          <Input
                            type="number"
                            step="1"
                            value={basePrice + (parseFloat(option.price_impact) || 0)}
                            onChange={(e) => {
                              const finalPrice = parseFloat(e.target.value) || 0;
                              // Back-calculate price_impact from final price
                              const newImpact = finalPrice - basePrice;
                              // Enforce: 1st option = 0 impact
                              if (optionIndex === 0) return;
                              updateOption(variantIndex, optionIndex, 'price_impact', newImpact);
                            }}
                            disabled={optionIndex === 0}
                            className={`pl-6 w-28 ${optionIndex === 0 ? 'bg-gray-100 text-gray-400' : ''}`}
                            title={optionIndex === 0 ? 'First option price = base price' : 'Set the final selling price for this option'}
                          />
                        </div>
                      </div>
                      
                      {/* Delete Option */}
                      <div className="flex justify-end sm:block">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(variantIndex, optionIndex)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="ml-1 sm:hidden">Remove</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Option Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addOption(variantIndex)}
                  disabled={(variant.options?.length || 0) >= maxOptions}
                  className="mt-3 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </Button>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* SKU Table - Generated Combinations */}
      {variants.length > 0 && variantCombinations.length > 0 && (
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  SKU Inventory ({variantCombinations.length} combinations)
                </CardTitle>
                <CardDescription className="hidden sm:block">
                  Set price, stock, and SKU for each variant combination
                </CardDescription>
              </div>
              
              {/* Total Stock Display */}
              <div className="flex items-center justify-between sm:justify-end gap-4">
                <div className="text-left sm:text-right">
                  <div className="text-xs text-gray-500 uppercase">Total Stock</div>
                  <div className="text-xl sm:text-2xl font-bold text-blue-600 flex items-center gap-1">
                    <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
                    {totalStock.toLocaleString('en-IN')}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSkuTable(!showSkuTable)}
                >
                  {showSkuTable ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>

          {showSkuTable && (
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Image</TableHead>
                      {variants.map((v, i) => (
                        <TableHead key={v.name || `variant-${i}`} className="min-w-[80px]">
                          {v.name || `Variant ${i + 1}`}
                        </TableHead>
                      ))}
                      <TableHead className="w-[100px]">SKU Code</TableHead>
                      <TableHead className="w-[90px]">
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          Price (₹)
                        </div>
                      </TableHead>
                      <TableHead className="w-[110px]" data-testid="combo-compare-at-header">
                        <div className="flex items-center gap-1" title="Strike-through price (MRP). Shown only when set on every variant — leave any blank to suppress all.">
                          <DollarSign className="h-3 w-3 text-amber-500" />
                          Compare ₹
                        </div>
                      </TableHead>
                      <TableHead className="w-[80px]">Stock</TableHead>
                      <TableHead className="w-[100px]">HSN Code</TableHead>
                      <TableHead className="w-[60px]">Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variantCombinations.map((combo) => {
                      const comboImage = getCombinationImage(combo);
                      return (
                        <TableRow 
                          key={combo.combination_key} 
                          className={!combo.is_available ? 'opacity-50 bg-gray-50' : ''}
                        >
                          <TableCell>
                            <div className="h-8 w-8 bg-gray-100 rounded overflow-hidden">
                              {comboImage ? (
                                <img src={comboImage} alt="variant" className="h-full w-full object-cover" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <ImageIcon className="h-3 w-3 text-gray-400" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          {variants.map((v, vIndex) => (
                            <TableCell key={vIndex} className="font-medium text-sm">
                              {combo.options?.[v.name] || '-'}
                            </TableCell>
                          ))}
                          <TableCell>
                            <Input
                              value={combo.sku || ''}
                              onChange={(e) => updateCombination(combo.combination_key, 'sku', e.target.value)}
                              placeholder="SKU"
                              className="w-[90px] h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={combo.price ?? basePrice}
                              onChange={(e) => updateCombination(combo.combination_key, 'price', parseFloat(e.target.value) || 0)}
                              className="w-[80px] h-8"
                              data-testid={`combo-price-${combo.combination_key}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={combo.compare_at_price ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                updateCombination(
                                  combo.combination_key,
                                  'compare_at_price',
                                  v === '' ? null : parseFloat(v) || 0
                                );
                              }}
                              placeholder="MRP"
                              className="w-[100px] h-8 text-amber-700"
                              data-testid={`combo-compare-at-${combo.combination_key}`}
                              title="Strike-through price. Set on every variant or leave all blank — partial values suppress strikes platform-wide (iter-221b rule)."
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={combo.stock || 0}
                              onChange={(e) => updateCombination(combo.combination_key, 'stock', parseInt(e.target.value) || 0)}
                              className={`w-[70px] h-8 ${(combo.stock || 0) === 0 ? 'border-amber-400 bg-amber-50' : ''}`}
                              data-testid={`combo-stock-${combo.combination_key}`}
                              title={(combo.stock || 0) === 0 ? 'Stock = 0 will publish as Sold Out on Shopify. Set a positive number, or enable "Continue selling when sold out" in the Inventory section.' : ''}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={combo.hsn_code || ''}
                              onChange={(e) => updateCombination(combo.combination_key, 'hsn_code', e.target.value)}
                              placeholder="HSN"
                              className="w-[90px] h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={combo.is_available !== false}
                              onCheckedChange={(checked) => updateCombination(combo.combination_key, 'is_available', checked)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Summary Footer */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                <div className="text-sm text-blue-700">
                  <strong>Auto-Calculated:</strong> Total inventory is the sum of all SKU stock levels
                </div>
                <Badge variant="secondary" className="text-lg px-4 py-1">
                  {totalStock.toLocaleString('en-IN')} units total
                </Badge>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Empty State - Simple without example since modal has live preview */}
      {variants.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8">
            <div className="text-center">
              <Layers className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Variants Defined</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add variants like {listingType === 'service' ? 'Duration, Package Type' : 'Size, Color, Material'} to create multiple options
              </p>
              <Button onClick={addVariant} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add First Variant
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VariantManagerV2;
